import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

function ImageWindow({ width = 400, height = 300, initialPosition, title, content = "", imgSource, onClose }) {

    const [position, setPosition] = useState(initialPosition ?? { x: 200, y: 150 });
    const [isDragging, setIsDragging] = useState(false);

    const dragOffset = useRef({ x: 0, y: 0 });
    const windowRef = useRef(null);

    const handleMouseDown = useCallback((e) => {
        if (e.button !== 0) return;

        const rect = windowRef.current?.getBoundingClientRect();

        if (rect) {
            dragOffset.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };
        }

        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return;

        setPosition({
            x: e.clientX - dragOffset.current.x,
            y: e.clientY - dragOffset.current.y,
        });
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
        }

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return (
        <>
            <motion.div
                ref={windowRef}
                className="fixed z-[70] overflow-hidden"
                initial={{ scale: 0.8, opacity: 0, y: 40 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.85, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                style={{
                    width,
                    height,
                    left: position.x,
                    top: position.y,
                    borderRadius: 10,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                    cursor: isDragging ? "grabbing" : "default",
                }}
                onClick={(e) => e.stopPropagation()}
            >

                {/* Window Header */}
                <div
                    className="relative flex items-center bg-slate-100/80 backdrop-blur-sm px-4 pt-3 pb-2 cursor-grab"
                    onMouseDown={handleMouseDown}
                >

                    {/* macOS Buttons */}
                    <div className="flex items-center gap-2 z-10">
                        <div className="w-3 h-3 rounded-full bg-[#ff6157] border border-[#e24640]" />
                        <div className="w-3 h-3 rounded-full bg-[#ffbd2e] border border-[#dea123]" />
                        <div className="w-3 h-3 rounded-full bg-[#28c940] border border-[#1bac2c]" />
                    </div>

                    {/* Title */}
                    <h1 className="absolute left-1/2 -translate-x-1/2 text-[13px] font-bold text-black/70 pointer-events-none font-applefont">
                        {title}
                    </h1>

                </div>

                {/* Window Content */}
                {content ? (

                    <div className="p-8 h-full overflow-auto bg-white font-applefont">

                        <h1 className="text-[28px] italic mb-4" style={{ fontFamily: '"Instrument Serif", "Times New Roman", serif', fontStyle: "italic", fontWeight: 400, color: "#000", }} >About Me</h1>

                        <p className="text-[14px] leading-[1.6] mb-3">
                            {content}
                        </p>

                        <p className="text-[14px] leading-[1.6] mb-3">
                            I care about interfaces that feel intentional, code that stays
  simple, and products that solve an actual problem.
                        </p>

                        <div className="flex gap-4 mt-6">

                            <a
                                href="mailto:hi.badushapareed@gmail.com"
                                className="px-4 py-2 rounded-lg text-[13px] bg-black text-white"
                            >
                                Contact Me
                            </a>

                            <a
                                href="https://badushapareed.in/resume.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-4 py-2 rounded-lg text-[13px] border border-black/20"
                            >
                                View Resume
                            </a>

                        </div>

                    </div>

                ) : (

                    <div className="w-full h-full flex items-center justify-center bg-white">
                        <img
                            className="w-full h-full object-cover"
                            src={imgSource}
                            alt="img"
                        />
                    </div>

                )}

            </motion.div>
        </>
    );
}


function SpotifyWindow({ initialPosition }) {

    const [position, setPosition] = useState(initialPosition ?? { x: 200, y: 150 });
    const [isDragging, setIsDragging] = useState(false);

    const dragOffset = useRef({ x: 0, y: 0 });
    const windowRef = useRef(null);

    const handleMouseDown = useCallback((e) => {
        if (e.button !== 0) return;

        const rect = windowRef.current?.getBoundingClientRect();

        if (rect) {
            dragOffset.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };
        }

        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return;

        setPosition({
            x: e.clientX - dragOffset.current.x,
            y: e.clientY - dragOffset.current.y,
        });
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
        }

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return (
        <motion.div
            ref={windowRef}
            className="fixed z-[70]"
            initial={{ scale: 0.8, opacity: 0, y: 40 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            style={{
                left: position.x,
                top: position.y,
                borderRadius: 10,
                boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            }}
        >

            <div onMouseDown={handleMouseDown}>
                <iframe
                    title="My-Song"
                    data-testid="embed-iframe"
                    style={{ borderradius: "12px" }}
                    src="https://open.spotify.com/embed/track/1golLrmqyxDWedF7YUHCOD?utm_source=generator&theme=0"
                    width="350"
                    height="152"
                    allowfullscreen=""
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                />
            </div>

        </motion.div>
    );
}



export default function AboutWindows({ onClose }) {
    const [windowSize, setWindowSize] = useState({ width: 1200, height: 800 });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <>

            <div
                className="fixed inset-0 z-[60]"
                style={{ backgroundColor: "rgba(0,0,0,0.09)" }}
                onClick={onClose}
            />
            <AnimatePresence>

                <div
                    className="fixed inset-0 z-[60]"
                    style={{ backgroundColor: "rgba(0,0,0,0.07)" }}
                    onClick={onClose}
                />

                <ImageWindow
                    width={400}
                    height={300}
                    title="img_2.heic"
                    imgSource="https://ik.imagekit.io/badu01/imgs/IMG-23_11.webp"
                    initialPosition={{
                        x: windowSize.width / 2 + 370,
                        y: windowSize.height / 2 - 350,
                    }}
                />

                <ImageWindow
                    width={400}
                    height={500}
                    title="img_1.heic"
                    imgSource="https://ik.imagekit.io/badu01/imgs/IMG-23_22.webp"
                    initialPosition={{
                        x: windowSize.width / 2 - 480,
                        y: windowSize.height / 2 - 303,
                    }}
                />

                <ImageWindow
                    content="I'm Badusha, a Computer Science graduate who likes turning
                            ideas into software — mostly through frontend, Flutter,
                            and the occasional machine learning or security project."
                    width={400}
                    height={450}
                    title="About Me"
                    initialPosition={{ x: 550, y: 100 }}
                />

                <SpotifyWindow
                    initialPosition={{ x: 1000, y: 400 }}
                />

            </AnimatePresence>
        </>
    );
}